/**
 * @file Container component for search result items with navigation and swipeable actions.
 * Handles player search results display with dynamic metrics and navigation to player pages.
 * Swipe search action navigates to the player detail page with "Similar Players" tab active.
 * When navigating from the standalone /player-search screen (iOS), uses router.replace()
 * to avoid the stacked screen blocking visibility of navigation in the tabs navigator.
 */

import { useReactiveVar } from "@apollo/client";
import { differenceInMonths } from "date-fns";
import { Href, usePathname, useRouter } from "expo-router";
import React, { memo, useCallback, useMemo, useRef, useState } from "react";

import AppleStyleSwipeableRow from "@/components/molecules/AppleStyleSwipeableRow";
import SearchResultsItemView from "./SearchResultsItemView";
import { CustomPromptRefProps } from "@/components/molecules/CustomPrompt/CustomPromptContainer";
import { IInfoBadge } from "@/components/organisms/InfoPlayer/InfoPlayerView";

import { useCurrentUser } from "@/features/auth";
import {
  CurrencyCode,
  PlayerSearchPlayerFragment,
  SimilarPlayerSortField,
  SortField,
} from "@/generated/graphql";
import useCreateWatchlistActions from "@/hooks/useCreateWatchlistActions";
import useExpandedPlayerUrl from "@/hooks/useExpandedPlayerUrl";
import useMobileScreen from "@/hooks/useMobileScreen";
import { useTheme } from "@/providers/ThemeProvider";
import {
  defaultSelectFocus,
  focusSelectedVar,
} from "@/stores/isFocusSearchInput";
import { selectPlayerTab } from "@/stores/player";
import {
  similarityShowVar,
  similaritySortVar,
  watchlistShowVar,
  watchlistSortVar,
} from "@/stores/selectionSort";
import {
  extractCustomMetricId,
  isCustomMetricSort,
} from "@/utils/customMetricsSorting";

/**
 * Props for SearchResultsItemContainer component
 */
interface Props {
  item: PlayerSearchPlayerFragment;
  score?: number | null;
  goToDetailPlayer?: () => void;
  goToSimilarPlayers?: () => void;
  onPressSimilarPlayer?: (item: PlayerSearchPlayerFragment) => void;
}

/**
 * Container component for search result item with swipeable actions and metric display.
 * Handles navigation to player detail and similar players pages using nested route structure.
 * @param root0 - Component props
 * @param root0.item - Player search result object containing profile data
 * @param root0.score - Similarity score for the player (0-100 scale)
 * @param root0.goToDetailPlayer - Callback invoked when navigating to player detail page
 * @param root0.goToSimilarPlayers - Callback invoked when navigating to player detail with Similar Players tab
 * @param root0.onPressSimilarPlayer - Callback invoked when player item is pressed with player object
 * @remarks
 * Swipe search action remembers "similarPlayers" for the player and navigates
 * to the player detail page. Provides swipeable actions on mobile.
 * Displays dynamic metrics based on current sort and show settings from reactive variables.
 */
const SearchResultsItemContainer = ({
  item,
  score,
  goToDetailPlayer,
  goToSimilarPlayers,
  onPressSimilarPlayer,
}: Props) => {
  const promptRef = useRef<CustomPromptRefProps>(
    null
  ) as React.RefObject<CustomPromptRefProps>;
  const [isDisabled, setIsDisabled] = useState(false);
  const router = useRouter();
  const pathname = usePathname();
  const isStandaloneSearch = pathname === "/player-search";
  const currentUser = useCurrentUser();
  const focusSelected = useReactiveVar(focusSelectedVar);
  const expandedPlayerDetailUrl = useExpandedPlayerUrl(`/players/${item?.id}`);
  // When navigating from filter results, always go to main /players route
  // to avoid broken left panel in /search context
  const playerDetailUrl =
    focusSelected.type === "filter"
      ? `/players/${item?.id}`
      : expandedPlayerDetailUrl;
  const currencyCode =
    currentUser?.organization?.currencyCode ?? CurrencyCode.Eur;
  const watchlistShowsSetting = useReactiveVar(watchlistShowVar);
  const watchlistSortValues = useReactiveVar(watchlistSortVar);
  const similarityShowsSetting = useReactiveVar(similarityShowVar);
  const similaritySortValues = useReactiveVar(similaritySortVar);
  const { handleAddWatchlistPress, loading } = useCreateWatchlistActions({
    promptRefForModal: promptRef,
  });

  const [showActionsheet, setShowActionsheet] = useState(false);

  const isPlayerInAnyWatchlist = Boolean(item?.isWatchedByCurrentUser);
  const playerName = useMemo(() => item?.name ?? "", [item]);
  const isSmallScreen = useMobileScreen();
  const { resolvedTheme } = useTheme();
  const isDark = useMemo(() => resolvedTheme === "dark", [resolvedTheme]);

  const infoBadge: IInfoBadge = useMemo(() => {
    const isSimilarity = score && score < 100;
    const showSetting = isSimilarity
      ? similarityShowsSetting
      : watchlistShowsSetting;
    const sortValues = isSimilarity
      ? similaritySortValues
      : watchlistSortValues;

    // Use sortValues if available, otherwise fallback to showSetting
    const currentSortField = sortValues?.sortBy || showSetting.type;
    const currentSortFieldStr = String(currentSortField);

    // Check if current sort is a custom metric
    if (isCustomMetricSort(currentSortFieldStr)) {
      const customMetricId = extractCustomMetricId(currentSortFieldStr);
      const customStats = item?.bioData?.customStats;
      const customValue = customStats?.[customMetricId];

      // If custom metric value is available, show it
      if (customValue) {
        return {
          value: parseFloat(customValue.value),
          type: currentSortField as SortField,
          min: 0,
          max: 100,
        };
      }
      // Fall back to GPR if custom metric value is not available
      return {
        value: item?.bioData?.gpr,
        type: SortField.Gpr,
        min: 30,
        max: 100,
      };
    }

    // Handle standard fields - support both SortField (uppercase) and SimilarPlayerSortField (lowercase)
    switch (currentSortField) {
      case SortField.FitScore:
      case SimilarPlayerSortField.FitScore:
        return {
          value: item?.bioData?.fitScore,
          type: SortField.FitScore,
          min: 40,
          max: 100,
        };
      case SortField.TeamStyleFit:
      case SimilarPlayerSortField.TeamStyleFit:
        return {
          value: item?.bioData?.teamStyleFit,
          type: SortField.TeamStyleFit,
          min: 30,
          max: 100,
        };
      case SortField.Carrying:
      case SimilarPlayerSortField.Carrying:
        return {
          value: item?.bioData?.carryingTransGlobalCategoricalScore,
          type: SortField.Carrying,
          min: 40,
          max: 85,
        };
      case SortField.Dribbling:
      case SimilarPlayerSortField.Dribbling:
        return {
          value: item?.bioData?.dribblingTransGlobalCategoricalScore,
          type: SortField.Dribbling,
          min: 40,
          max: 85,
        };
      case SortField.Passing:
      case SimilarPlayerSortField.Passing:
        return {
          value: item?.bioData?.passingTransGlobalCategoricalScore,
          type: SortField.Passing,
          min: 40,
          max: 85,
        };
      case SortField.Shooting:
      case SimilarPlayerSortField.Shooting:
        return {
          value: item?.bioData?.shotLocationTransGlobalCategoricalScore,
          type: SortField.Shooting,
          min: 40,
          max: 85,
        };
      case SortField.GkPassing:
      case SimilarPlayerSortField.GkPassing:
        return {
          value: item?.bioData?.gkPassingTransCategoricalScore,
          type: SortField.GkPassing,
          min: 40,
          max: 85,
        };
      case SortField.GkPositioning:
      case SimilarPlayerSortField.GkPositioning:
        return {
          value: item?.bioData?.gkPositioningTransCategoricalScore,
          type: SortField.GkPositioning,
          min: 40,
          max: 85,
        };
      case SortField.GkShotStopping:
      case SimilarPlayerSortField.GkShotStopping:
        return {
          value: item?.bioData?.gkShotStoppingTransCategoricalScore,
          type: SortField.GkShotStopping,
          min: 40,
          max: 85,
        };
      case SortField.PlayerValuation:
      case SimilarPlayerSortField.PlayerValuation:
        return {
          value: item?.bioData?.playerValuation,
          type: SortField.PlayerValuation,
          min: 0,
          max: 1000000000,
          isDefaultStyle: true,
        };
      case SortField.MonthsRemaining:
      case SimilarPlayerSortField.MonthsRemaining:
        if (item?.bioData?.contractExpires) {
          const contractExpires = new Date(
            item?.bioData?.contractExpires
          ).toISOString();
          return {
            value: differenceInMonths(contractExpires, new Date()),
            type: SortField.MonthsRemaining,
            min: 18,
            max: 6,
          };
        }
        return {
          value: null,
          type: SortField.MonthsRemaining,
          min: 18,
          max: 6,
        };
      case SortField.Gpr:
      case SimilarPlayerSortField.Gpr:
      default:
        return {
          value: item?.bioData?.gpr,
          type: SortField.Gpr,
          min: 30,
          max: 100,
        };
    }
  }, [
    item?.bioData?.carryingTransGlobalCategoricalScore,
    item?.bioData?.contractExpires,
    item?.bioData?.customStats,
    item?.bioData?.dribblingTransGlobalCategoricalScore,
    item?.bioData?.fitScore,
    item?.bioData?.gkPassingTransCategoricalScore,
    item?.bioData?.gkPositioningTransCategoricalScore,
    item?.bioData?.gkShotStoppingTransCategoricalScore,
    item?.bioData?.gpr,
    item?.bioData?.passingTransGlobalCategoricalScore,
    item?.bioData?.playerValuation,
    item?.bioData?.shotLocationTransGlobalCategoricalScore,
    item?.bioData?.teamStyleFit,
    score,
    similarityShowsSetting,
    similaritySortValues,
    watchlistShowsSetting,
    watchlistSortValues,
  ]);

  const handleAddPress = useCallback(() => setShowActionsheet(true), []);
  const handleClose = useCallback(() => setShowActionsheet(false), []);
  const handleOpenSwipe = useCallback(() => setIsDisabled(true), []);
  const handleCloseSwipe = useCallback(() => setIsDisabled(false), []);
  const handleSearch = useCallback(() => {
    const playerId = item?.id;
    if (!playerId) {
      console.error("Cannot navigate to similar players: item ID is missing");
      return;
    }
    // SE-8285: the standalone branch replaces the route, so the writer and the
    // reader are never alive together — naming the player is what carries the
    // selection across.
    selectPlayerTab(playerId, "similarPlayers");
    if (isStandaloneSearch) {
      router.replace(`/search/players/${playerId}` as Href);
    } else {
      router.push(playerDetailUrl as Href);
    }
    goToSimilarPlayers?.();
  }, [
    goToSimilarPlayers,
    isStandaloneSearch,
    item?.id,
    playerDetailUrl,
    router,
  ]);

  const handleStartShouldSetResponder = useCallback(() => false, []);

  const handlePress = useCallback(() => {
    if (isDisabled) {
      setIsDisabled(false);
      return;
    }
    if (!isSmallScreen) {
      onPressSimilarPlayer?.(item);
    }
    // Reset focus state so left panel shows roster instead of empty filter results
    focusSelectedVar(defaultSelectFocus);
    goToDetailPlayer?.();
    if (isStandaloneSearch) {
      router.replace(`/search/players/${item?.id}` as Href);
    } else {
      router.push(playerDetailUrl as Href);
    }
  }, [
    isDisabled,
    isSmallScreen,
    isStandaloneSearch,
    onPressSimilarPlayer,
    item,
    goToDetailPlayer,
    router,
    playerDetailUrl,
  ]);

  return !isSmallScreen ? (
    <SearchResultsItemView
      item={item}
      playerName={playerName}
      showActionsheet={showActionsheet}
      isPlayerInAnyWatchlist={isPlayerInAnyWatchlist}
      onAddPress={handleAddPress}
      onClose={handleClose}
      onAddWatchlistPress={handleAddWatchlistPress}
      loading={loading}
      score={score}
      onPress={handlePress}
      onStartShouldSetResponder={handleStartShouldSetResponder}
      promptRef={promptRef}
      similarityShowsSetting={similarityShowsSetting}
      infoBadge={infoBadge}
      currencyCode={currencyCode}
      isDark={isDark}
    />
  ) : (
    <AppleStyleSwipeableRow
      onSearch={loading ? undefined : handleSearch}
      isPlayerInAnyWatchlist={isPlayerInAnyWatchlist}
      onCallbackOpen={handleOpenSwipe}
      onCallbackClose={handleCloseSwipe}
    >
      <SearchResultsItemView
        item={item}
        playerName={playerName}
        showActionsheet={showActionsheet}
        isPlayerInAnyWatchlist={isPlayerInAnyWatchlist}
        onAddPress={handleAddPress}
        onClose={handleClose}
        onAddWatchlistPress={handleAddWatchlistPress}
        loading={loading}
        score={score}
        onPress={handlePress}
        onStartShouldSetResponder={handleStartShouldSetResponder}
        promptRef={promptRef}
        similarityShowsSetting={similarityShowsSetting}
        infoBadge={infoBadge}
        currencyCode={currencyCode}
        isDark={isDark}
      />
    </AppleStyleSwipeableRow>
  );
};

export default memo(SearchResultsItemContainer);
